import type { ListingPublicationStep } from '@/lib/presets/schema/listing-publication-template';
import type { DateTime } from 'luxon';

type StepTiming = Pick<
  ListingPublicationStep,
  'startDay' | 'startTime' | 'days' | 'endTime'
>;

export function parseTime(time: string): { hour: number; minute: number } {
  const [hour, minute] = time.split(':').map(Number);
  return { hour: hour ?? 0, minute: minute ?? 0 };
}

function minutesAt(day: number, time: string): number {
  const { hour, minute } = parseTime(time);
  return day * 24 * 60 + hour * 60 + minute;
}

/** The step's end day relative to Dag 0, or null when it runs tills vidare. */
export function stepEndDay(step: StepTiming): number | null {
  return step.days === null ? null : step.startDay + step.days;
}

/** Open-ended steps always pass; closed steps must end after they start. */
export function stepEndsAfterStart(step: StepTiming): boolean {
  const endDay = stepEndDay(step);
  if (endDay === null) return true;
  if (step.endTime === null) return false;
  return (
    minutesAt(endDay, step.endTime) > minutesAt(step.startDay, step.startTime)
  );
}

/**
 * Resolves a step to absolute times for a given Dag 0. Days are added as
 * calendar days in `day0`'s zone, so a DST switch keeps the clock time.
 */
export function resolveStep(
  step: StepTiming,
  day0: DateTime
): { publishAt: DateTime; unpublishAt: DateTime | null } {
  const base = day0.startOf('day');
  const publishAt = base
    .plus({ days: step.startDay })
    .set(parseTime(step.startTime));

  const endDay = stepEndDay(step);
  const unpublishAt =
    endDay === null || step.endTime === null
      ? null
      : base.plus({ days: endDay }).set(parseTime(step.endTime));

  return { publishAt, unpublishAt };
}
